import { Action } from "../core/action";
import type { KarelModelShape } from "../core/karelModel";
import type { Karel } from "./karel";
import { translatePython } from "../util/pythonTranspiler";
import { camelToSnake } from "../util/util";

export interface KarelEvalEngine {
  compile(code: string, inputs?: string[]): void;
  executeStep(): boolean;
  getOutput(): string[];
}

export interface KarelEvalEngineOptions {
  language?: "js" | "python";
  /** Called each time a buffered `print()` action is replayed. */
  onOutput?: (text: string) => void;
}

/**
 * Runs student code in an `eval` against a deep copy of the Karel model while
 * buffering the actions to animate. Implements the same interface as the
 * (unused) compiled engine.
 */
export function KarelEvalEngine(
  karel: Karel,
  options: KarelEvalEngineOptions = {},
): KarelEvalEngine {
  const language = options.language ?? "js";
  const MOVE = 0;
  const TURN_LEFT = 1;
  const TURN_RIGHT = 2;
  const PUT_BEEPER = 3;
  const PICK_BEEPER = 4;
  const PAINT_CORNER = 5;
  const DISPLAY = 6;

  const MAX_ACTIONS = 10000;

  let actionBuffer: Action[] = [];
  let actionIndex = 0;
  let virtualKarelModel: KarelModelShape | null = null;
  let outputLines: string[] = [];
  let inputValues: string[] = [];
  let inputIndex = 0;
  let seededRandom: (() => number) | null = null;

  const that: KarelEvalEngine = {
    compile(code: string, inputs: string[] = []): void {
      virtualKarelModel = karel.getModel().deepCopy();
      actionBuffer = [];
      actionIndex = 0;
      outputLines = [];
      inputValues = inputs;
      inputIndex = 0;
      seededRandom = null;
      runStudentCode(code);
    },

    executeStep(): boolean {
      if (actionIndex >= actionBuffer.length || actionIndex === -1) {
        return true;
      }

      const action = actionBuffer[actionIndex];
      const actionId = action.getId();
      actionIndex = actionIndex + 1;

      switch (actionId) {
        case MOVE:
          karel.move();
          break;
        case TURN_LEFT:
          karel.turnLeft();
          break;
        case TURN_RIGHT:
          karel.turnRight();
          break;
        case PUT_BEEPER:
          karel.putBeeper();
          break;
        case PICK_BEEPER:
          karel.pickBeeper();
          break;
        case PAINT_CORNER:
          karel.paintCorner(action.getParam() as string);
          break;
        case DISPLAY:
          options.onOutput?.(action.getParam() as string);
          break;
        default:
          throw new Error("Invalid action");
      }

      return false;
    },

    getOutput(): string[] {
      return [...outputLines];
    },
  };

  function addToActionBuffer(action: Action): void {
    if (actionBuffer.length > MAX_ACTIONS) {
      throw new Error("infinite loop");
    }
    actionBuffer.push(action);
  }

  function turnRight(): void {
    virtualKarelModel!.turnRight();
    addToActionBuffer(new Action(TURN_RIGHT, null));
  }

  function turnAround(): void {
    turnLeft();
    turnLeft();
  }

  /**
   * Small deterministic PRNG (mulberry32) used by `randomSeed(n)`. When no
   * seed has been set, `nextRandom()` falls back to `Math.random()`.
   */
  function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return function next(): number {
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function nextRandom(): number {
    return seededRandom ? seededRandom() : Math.random();
  }

  function randomSeed(seed: number): void {
    seededRandom = mulberry32(seed);
  }

  /**
   * AP CSP `RANDOM(a, b)` returns an integer from `a` to `b` inclusive.
   * `random()` with no arguments remains a 50/50 boolean.
   */
  function random(...args: number[]): boolean | number {
    if (args.length === 0) {
      return nextRandom() < 0.5;
    }
    if (args.length !== 2) {
      throw new Error("random() takes either no arguments or (min, max)");
    }
    const lo = Math.ceil(Math.min(args[0], args[1]));
    const hi = Math.floor(Math.max(args[0], args[1]));
    return lo + Math.floor(nextRandom() * (hi - lo + 1));
  }

  function canMove(direction: string): boolean {
    return virtualKarelModel!.canMove(direction);
  }

  function karelAvenue(): number {
    return virtualKarelModel!.getKarelCol() + 1;
  }

  function karelStreet(): number {
    return virtualKarelModel!.getNumRows() - virtualKarelModel!.getKarelRow();
  }

  function formatValue(value: unknown): string {
    if (value === null || value === undefined)
      return language === "python" ? "None" : "null";
    if (typeof value === "boolean") {
      if (language === "python") return value ? "True" : "False";
      return String(value);
    }
    return String(value);
  }

  function print(...args: unknown[]): void {
    const text = args.map(formatValue).join(" ");
    outputLines.push(text);
    addToActionBuffer(new Action(DISPLAY, text));
  }

  function input(): string {
    if (inputIndex >= inputValues.length) {
      throw new Error(
        "input() was called but no more input values are available",
      );
    }
    const value = inputValues[inputIndex];
    inputIndex += 1;
    return value;
  }

  function lengthOf(value: unknown): number {
    if (typeof value === "string") return value.length;
    if (Array.isArray(value)) return value.length;
    throw new Error("len() expects a list or a string");
  }

  function toInt(value: unknown): number {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(n))
      throw new Error("int() could not convert " + String(value));
    return Math.trunc(n);
  }

  function toFloat(value: unknown): number {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(n))
      throw new Error("float() could not convert " + String(value));
    return n;
  }

  function toStr(value: unknown): string {
    return formatValue(value);
  }

  function paintCorner(color: string): void {
    addToActionBuffer(new Action(PAINT_CORNER, color));
    virtualKarelModel!.paintCorner(color);
  }

  function move(): void {
    addToActionBuffer(new Action(MOVE, null));
    try {
      virtualKarelModel!.move();
    } catch {
      // Move into a wall: record the action but do not advance the model.
    }
  }

  function turnLeft(): void {
    addToActionBuffer(new Action(TURN_LEFT, null));
    virtualKarelModel!.turnLeft();
  }

  function frontIsClear(): boolean {
    return virtualKarelModel!.frontIsClear();
  }

  function frontIsBlocked(): boolean {
    return !frontIsClear();
  }

  function putBeeper(): void {
    addToActionBuffer(new Action(PUT_BEEPER, null));
    virtualKarelModel!.putBeeper();
  }

  function pickBeeper(): void {
    addToActionBuffer(new Action(PICK_BEEPER, null));
    try {
      virtualKarelModel!.pickBeeper();
    } catch {
      // No beeper present: record the action but do not advance the model.
    }
  }

  function beepersPresent(): boolean {
    return virtualKarelModel!.beeperPresent();
  }

  function noBeepersPresent(): boolean {
    return !beepersPresent();
  }

  function leftIsClear(): boolean {
    return virtualKarelModel!.leftIsClear();
  }

  function leftIsBlocked(): boolean {
    return !leftIsClear();
  }

  function rightIsClear(): boolean {
    return virtualKarelModel!.rightIsClear();
  }

  function rightIsBlocked(): boolean {
    return !rightIsClear();
  }

  function facingEast(): boolean {
    return virtualKarelModel!.facingEast();
  }

  function notFacingEast(): boolean {
    return !facingEast();
  }

  function facingWest(): boolean {
    return virtualKarelModel!.facingWest();
  }

  function notFacingWest(): boolean {
    return !facingWest();
  }

  function facingNorth(): boolean {
    return virtualKarelModel!.facingNorth();
  }

  function notFacingNorth(): boolean {
    return !facingNorth();
  }

  function facingSouth(): boolean {
    return virtualKarelModel!.facingSouth();
  }

  function notFacingSouth(): boolean {
    return !facingSouth();
  }

  /**
   * Builds the command surface exposed to student programs. Every canonical
   * camelCase name also gets its Pythonic snake_case alias automatically, so
   * the two naming styles can never drift.
   */
  function buildCommandTable(): Record<string, (...args: any[]) => unknown> {
    const base: Array<[string, (...args: any[]) => unknown]> = [
      ["move", move],
      ["turnLeft", turnLeft],
      ["turnRight", turnRight],
      ["turnAround", turnAround],
      ["putBeeper", putBeeper],
      ["pickBeeper", pickBeeper],
      ["paintCorner", paintCorner],
      ["random", random],
      ["randomSeed", randomSeed],
      ["frontIsClear", frontIsClear],
      ["frontIsBlocked", frontIsBlocked],
      ["leftIsClear", leftIsClear],
      ["leftIsBlocked", leftIsBlocked],
      ["rightIsClear", rightIsClear],
      ["rightIsBlocked", rightIsBlocked],
      ["beepersPresent", beepersPresent],
      ["noBeepersPresent", noBeepersPresent],
      ["canMove", canMove],
      ["karelAvenue", karelAvenue],
      ["karelStreet", karelStreet],
      ["facingNorth", facingNorth],
      ["notFacingNorth", notFacingNorth],
      ["facingEast", facingEast],
      ["notFacingEast", notFacingEast],
      ["facingSouth", facingSouth],
      ["notFacingSouth", notFacingSouth],
      ["facingWest", facingWest],
      ["notFacingWest", notFacingWest],
      ["print", print],
      ["input", input],
      ["len", lengthOf],
      ["length", lengthOf],
      ["int", toInt],
      ["float", toFloat],
      ["str", toStr],
    ];
    const commands: Record<string, (...args: any[]) => unknown> = {};
    for (const [name, impl] of base) {
      commands[name] = impl;
      commands[camelToSnake(name)] = impl;
    }
    return commands;
  }

  /**
   * Builds the student's program with the Karel commands bound as ordinary
   * function parameters. Using `new Function` with explicit parameter names
   * (rather than a scope-based `eval`) keeps the API stable even when the
   * production minifier renames the local declarations.
   */
  function runStudentCode(code: string): void {
    const commands = buildCommandTable();

    const names = Object.keys(commands);
    const source = translateStudentCode(code);
    // The compiled path auto-invokes `main`; mirror that here so a program that
    // only *defines* `function main()` still runs when the student clicks Run.

    const program = new Function(
      ...names,
      source + '\nif (typeof main === "function") { main(); }',
    );
    program(...names.map((name) => commands[name]));
  }

  function translateStudentCode(code: string): string {
    if (language === "python") return translatePython(code);
    return translateRepeatSyntax(code);
  }

  /**
   * The Karel language supports `repeat(n) { ... }`, which is not valid
   * JavaScript. Translate it to an equivalent `for` loop so the eval engine can
   * run repeat lessons without the full compiler.
   */
  function translateRepeatSyntax(code: string): string {
    let counter = 0;
    return code.replace(
      /repeat\s*\(\s*([^()]*)\s*\)\s*\{/g,
      (_match, count: string) => {
        const name = "__repeat" + counter++;
        return `for (let ${name} = 0; ${name} < (${count}); ${name}++) {`;
      },
    );
  }

  return that;
}
